import { Box, Stack, Typography } from '@mui/joy';
import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { Button } from 'core/components';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { MONTHS } from './constants';

interface IProps {
  currentDate: Dayjs;
  setCurrentDate: Dispatch<SetStateAction<Dayjs>>;
}

export const MonthPicker = ({ currentDate, setCurrentDate }: IProps) => {
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const onYearChange = useCallback(
    (direction: number) => {
      setCurrentDate((prev: Dayjs) => prev.add(direction, 'year'));
    },
    [setCurrentDate]
  );

  const onMonthSelect = useCallback(
    (monthIndex: number) => {
      setCurrentDate((prev) => prev.month(monthIndex));
      setShowMonthPicker(false);
    },
    [setCurrentDate]
  );

  return (
    <>
      <Button
        text={`${MONTHS[currentDate.month()]} ${currentDate.year()}`}
        color={Color.NEUTRAL}
        variant={Variant.PLAIN}
        onClick={() => setShowMonthPicker(!showMonthPicker)}
        endDecorator={<CaretDown weight="fill" />}
      />

      {showMonthPicker && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'background.body',
            border: '1px solid',
            borderColor: 'neutral.300',
            borderRadius: '6px',
            boxShadow: 'lg',
            zIndex: 10,
            padding: 2,
            minWidth: 200,
            mt: 0.5,
          }}
        >
          <Stack
            direction={Direction.ROW}
            sx={{ mb: 2, alignItems: 'center', justifyContent: 'center', gap: 1 }}
          >
            <Button
              size={Size.SM}
              onClick={() => onYearChange(-1)}
              icon={<CaretLeft weight="bold" />}
            />
            <Typography
              level={Level.BODY_MD}
              sx={{ fontWeight: 500, minWidth: 60, textAlign: 'center' }}
            >
              {currentDate.year()}
            </Typography>
            <Button
              size={Size.SM}
              onClick={() => onYearChange(1)}
              icon={<CaretRight weight="bold" />}
            />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 0.5,
            }}
          >
            {MONTHS.map((month: string, index: number) => (
              <Button
                key={month}
                text={month.slice(0, 3)}
                variant={index === currentDate.month() ? Variant.SOFT : Variant.PLAIN}
                color={index === currentDate.month() ? Color.PRIMARY : Color.NEUTRAL}
                size={Size.SM}
                onClick={() => onMonthSelect(index)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Close month picker on outside click */}
      {showMonthPicker && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5,
          }}
          onClick={() => setShowMonthPicker(false)}
        />
      )}
    </>
  );
};
